import { useState } from 'react';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import GenericModal from 'components/app-components/modals/GenericModal';
import { emailRegex } from 'utils/regex';
import ErrorMessage from 'components/app-components/form/RHFFieldErrorMessage';
import Button from 'components/app-components/ui/button';
import GreenCheck from 'icons/GreenCheck';
import { useTheraFernClient } from 'hooks/useFern';
import { formatErrorMessage, isEmailAlreadyInUseError } from 'utils/error';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
interface IFormValues {
  email?: string;
}

const schema: any = yup.object().shape({
  email: yup.string().required('Email is required').matches(emailRegex, 'Email is invalid'),
});

export const hasUsedEmailUpdateModal_LS_key = 'hasUsedEmailUpdateModal_LS_key';
export default function ChangeEmailModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { theraFernApi } = useTheraFernClient();
  const { user, logout } = useUser();
  const [isEmailChangeFormSubmitted, setIsEmailChangeFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    defaultValues: {
      email: '' as string,
    },
    resolver: yupResolver(schema) as Resolver<IFormValues>,
  });

  const onSubmit: SubmitHandler<IFormValues> = async (data) => {
    setIsSubmitting(true);
    await theraFernApi.thera.email.emailApi
      .updateUserEmail(user?.userID, { newEmail: data?.email.toLowerCase() })
      .then(async () => {
        localStorage.setItem(hasUsedEmailUpdateModal_LS_key, data?.email.toLowerCase());

        theraToast.success('Email sent');
        setIsEmailChangeFormSubmitted(true);
      })
      .catch((error) => {
        const errorMessage = formatErrorMessage(error, 'Failed to update the email');
        if (isEmailAlreadyInUseError(error)) {
          theraToast.warning(errorMessage, { id: 'change-email-error' });
          return;
        }
        theraToast.error({
          message: errorMessage,
          error: error,
          options: {
            id: 'change-email-error',
            sendToSlack: false,
          },
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onFinalFormContinue = async () => {
    if (isEmailChangeFormSubmitted) {
      // the stytch user object doesn't get mutated so we need to logout and reload
      await logout(true, true);
      window.location.reload();
    }
    onClose();
  };

  return (
    <GenericModal
      open={isOpen}
      onClose={onFinalFormContinue}
      showHeaderBorder={false}
      titleCenter
      title={`${isEmailChangeFormSubmitted ? '' : 'Change Email'}`}
      width="max-w-[540px] min-w-min"
      isProcessing={isSubmitting}
      preventOutsideClick={isEmailChangeFormSubmitted}
    >
      {isEmailChangeFormSubmitted ? (
        <div className="flex flex-col items-center gap-4">
          <div>
            <GreenCheck className="h-8 w-8" />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-center text-lg font-medium">Verification link sent to your new email address </p>
            <p className="px-14 text-center text-sm text-neutral-500">
              Click on the email to activate your new email. Once done, your new email will be taken into effect. You
              will be logged out after clicking continue.
            </p>
          </div>
          <Button htmlType="button" loading={isSubmitting} variant="primary" onClick={onFinalFormContinue}>
            Continue
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
          <div>
            <label htmlFor="email" className="form-label required">
              Enter New Email Address
            </label>
            <input
              type="email"
              id="email"
              className="form-input"
              placeholder="Enter New Email Address"
              {...register('email')}
            />
            <ErrorMessage errors={errors} name="email" />
          </div>
          <div className="rounded-md border border-neutral-200 p-4">
            <p className="text-sm text-neutral-500">
              We’ll send you a verification link to the new email once you update your email, click on the link to
              activate your new email.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button htmlType="submit" loading={isSubmitting} variant="primary">
              Update
            </Button>
          </div>
        </form>
      )}
    </GenericModal>
  );
}
